import { isAfter, startOfDay } from 'date-fns'
import { useCallback, useEffect, useMemo } from 'react'

import { useUpdateConfiguredServiceMutation } from 'api/configured-service/configuredServiceCrud'
import { useCSDetailsURLData } from 'api/configured-service/useCSDetailsURLData'
import { useLazyFetchMetaFiltersQuery } from 'api/meta/getMetaFilters'
import { useGetTagsMetadataQuery } from 'api/tags/getTagsMetadata'
import { useServiceDetailsFormDefaults } from 'components/configured-service/accordions/ServiceDetailsAccordion/useServiceDetailsFormDefaults'
import { useServiceDetailsFormSubmission } from 'components/configured-service/accordions/ServiceDetailsAccordion/useServiceDetailsFormSubmission'
import { useCSDetailsCachedData } from 'components/configured-service/useCSDetailsCachedData'
import {
  selectDeactivateOnEndDate,
  selectEndDateAsDate,
  selectIsServiceDeactivated,
} from 'data/configured-service/configuredServiceDetailsSelectors'
import { handleError } from 'utils/error'

/**
 * Deactivating the service on screen. Shared so the header menu, the cancel-placement fork and the
 * drawer all end a service through one call.
 */
export const useDeactivateConfiguredService = (serviceId?: string) => {
  const { id: urlId } = useCSDetailsURLData()
  // Off the service page there is no id in the URL, so the caller names the service instead.
  const id = serviceId ?? urlId
  const { data } = useCSDetailsCachedData(serviceId ? { id: serviceId } : undefined)
  const [updateConfiguredService, { isLoading: isDeactivating }] = useUpdateConfiguredServiceMutation()
  const [fetchMetaFilters, { data: metaFiltersData }] = useLazyFetchMetaFiltersQuery()

  const isServiceDeactivated = useMemo(() => Boolean(selectIsServiceDeactivated(data)), [data])
  const endDate = useMemo(() => selectEndDateAsDate(data), [data])
  // A schedule is a flag AND a date. The flag can outlive the date — it survives an edit that clears
  // the end date — and on its own it schedules nothing, which is why `ScheduledDeactivationChip`
  // has always demanded both. The menu asked only for the flag and so offered to cancel nothing.
  const isDeactivationScheduled = useMemo(() => Boolean(selectDeactivateOnEndDate(data)) && Boolean(endDate), [data, endDate])

  const defaultFormValues = useServiceDetailsFormDefaults(data, metaFiltersData)
  const { formatFormData } = useServiceDetailsFormSubmission(id ?? '')

  useGetTagsMetadataQuery(undefined)

  useEffect(() => {
    fetchMetaFilters({ body: {} })
  }, [fetchMetaFilters])

  const toggleServiceStatus = useCallback(
    async () =>
      updateConfiguredService({
        ...formatFormData(defaultFormValues as Parameters<typeof formatFormData>[0]),
        deactivate: !isServiceDeactivated,
      }).unwrap(),
    [updateConfiguredService, formatFormData, defaultFormValues, isServiceDeactivated]
  )

  const deactivate = useCallback(async () => toggleServiceStatus().catch(handleError), [toggleServiceStatus])

  /**
   * Ending the service on the rep's own timing: `null` flips it inactive today, a date runs it until
   * then and leaves the flip to the nightly `deactivateOnEndDate` sweep.
   */
  const endService = useCallback(
    async (scheduledEndDate: Date | null) => {
      // An end date already behind us is the date this service ended; today would rewrite it.
      // (The backend deletes work orders past the end date, so no future event has to be cleared.)
      const alreadyEnded = Boolean(endDate && !isAfter(startOfDay(endDate), startOfDay(new Date())))
      const effectiveEndDate = scheduledEndDate ?? (alreadyEnded ? endDate : new Date())

      return updateConfiguredService(
        formatFormData({
          ...(defaultFormValues as Parameters<typeof formatFormData>[0]),
          endDate: effectiveEndDate,
          deactivateOnEndDate: Boolean(scheduledEndDate),
          deactivate: !scheduledEndDate,
        })
      ).unwrap()
    },
    [updateConfiguredService, formatFormData, defaultFormValues, endDate]
  )

  /**
   * Calls off a scheduled deactivation by clearing only the flag the nightly sweep reads. The end
   * date stays: a service is allowed to have one without switching itself off on it.
   */
  const cancelScheduledDeactivation = useCallback(
    async () =>
      updateConfiguredService(
        formatFormData({
          ...(defaultFormValues as Parameters<typeof formatFormData>[0]),
          deactivateOnEndDate: false,
        })
      ).unwrap(),
    [updateConfiguredService, formatFormData, defaultFormValues]
  )

  return {
    isServiceDeactivated,
    endDate,
    isDeactivationScheduled,
    cancelScheduledDeactivation,
    isDeactivating,
    toggleServiceStatus,
    deactivate,
    endService,
  }
}
